import React, { useEffect, useState } from 'react';
import { mentorApi, interviewerApi } from '../../services/api';
import Button from '../../components/UI/Button';
import { InterviewerItem } from '../../types';
import { canApproveInterviewers, canApproveMentors, canAssignProgramMentors } from '../../utils/roles';
import { useAuth } from '../../context/AuthContext';
import ActionStatus from '../../components/UI/ActionStatus';
import { useAutoClear } from '../../hooks/useAutoClear';
import ContributorProfileEditor, { MentorProfile } from '../../components/admin/ContributorProfileEditor';

type MentorRow = MentorProfile;

const ContributorApplicationsPanel: React.FC<{ kind: 'mentors' | 'interviewers' }> = ({ kind }) => {
  const { user } = useAuth();
  const [mentors, setMentors] = useState<MentorRow[]>([]);
  const [interviewers, setInterviewers] = useState<InterviewerItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [programs, setPrograms] = useState<Array<{ id: number; title: string }>>([]);
  const [selectedProgram, setSelectedProgram] = useState<number | ''>('');
  const [assigned, setAssigned] = useState<MentorRow[]>([]);
  const [actionKey, setActionKey] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<'view' | 'edit'>('view');
  const [editorMentor, setEditorMentor] = useState<MentorProfile | null>(null);
  const [editorInterviewer, setEditorInterviewer] = useState<InterviewerItem | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);
  useAutoClear(message, setMessage, 5000);

  const canEditMentors = canApproveMentors(user);
  const canEditInterviewers = canApproveInterviewers(user);

  const load = () => {
    if (kind === 'mentors') {
      mentorApi.list().then((r) => setMentors((r.data.mentors || []) as MentorRow[])).catch((err: unknown) => setError(err instanceof Error ? err.message : 'Could not load mentors.'));
    } else {
      interviewerApi.applications().then((r) => setInterviewers(r.data.interviewers || [])).catch((err: unknown) => setError(err instanceof Error ? err.message : 'Could not load interviewers.'));
    }
  };

  useEffect(() => { load(); }, [kind]);

  useEffect(() => {
    if (kind !== 'mentors' || !canAssignProgramMentors(user)) return;
    mentorApi.academyPrograms()
      .then((r) => setPrograms((r.data.courses || []).map((course) => ({ id: course.id, title: course.title || course.display_Title || `Program ${course.id}` }))))
      .catch(() => undefined);
  }, [kind, user]);

  const approveMentor = (id: number) => mentorApi.approve(id).then(() => { setActionKey(`mentor-${id}`); setMessage('Mentor approved. They receive the Mentor role.'); load(); });
  const rejectMentor = (id: number) => mentorApi.reject(id, 'Does not meet current program needs').then(() => { setActionKey(`mentor-${id}`); setMessage('Mentor rejected.'); load(); });
  const approveInterviewer = (id: number) => interviewerApi.approve(id).then(() => { setActionKey(`interviewer-${id}`); setMessage('Interviewer approved. They receive the Interviewer role.'); load(); });
  const rejectInterviewer = (id: number) => interviewerApi.reject(id, 'Does not match current interview demand').then(() => { setActionKey(`interviewer-${id}`); setMessage('Interviewer rejected.'); load(); });

  const openMentor = async (id: number, mode: 'view' | 'edit') => {
    setError(null);
    try {
      const response = await mentorApi.get(id);
      setEditorMentor((response.data.mentor || {}) as MentorProfile);
      setEditorMode(mode);
      setEditorOpen(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not load mentor profile.');
    }
  };

  const openInterviewer = async (id: number, mode: 'view' | 'edit') => {
    setError(null);
    try {
      const response = await interviewerApi.get(id);
      setEditorInterviewer(response.data.interviewer || null);
      setEditorMode(mode);
      setEditorOpen(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not load interviewer profile.');
    }
  };

  const saveMentor = async (profile: MentorProfile) => {
    if (!profile.mentorId) return;
    setSavingProfile(true);
    setError(null);
    try {
      await mentorApi.update(profile.mentorId, profile as Record<string, unknown>);
      setMessage('Mentor profile updated.');
      setEditorOpen(false);
      load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not update mentor profile.');
    } finally {
      setSavingProfile(false);
    }
  };

  const saveInterviewer = async (profile: InterviewerItem) => {
    setSavingProfile(true);
    setError(null);
    try {
      await interviewerApi.update(profile.interviewerId, profile);
      setMessage('Interviewer profile updated.');
      setEditorOpen(false);
      load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not update interviewer profile.');
    } finally {
      setSavingProfile(false);
    }
  };

  const loadAssigned = (courseId: number) => {
    mentorApi.programMentors(courseId).then((r) => setAssigned((r.data.mentors || []).map((row) => ({
      mentorId: Number(row.mentorId),
      fullName: String(row.mentorName || row.fullName || ''),
    })))).catch(() => setAssigned([]));
  };

  return (
    <div className="space-y-6">
      <p className="text-gray-600">
        {kind === 'mentors'
          ? 'View and edit mentor profiles (Owner, AcademyAdmin), then approve. Assign approved mentors to IDC Academy programs.'
          : 'View and edit interviewer profiles (Owner, InterviewAdmin), then approve. Approved interviewers can be mapped to interview requests.'}
      </p>
      {error && <p className="text-error-600 text-sm">{error}</p>}
      {message && !editorOpen && <p className="text-sm text-primary-700">{message}</p>}

      {kind === 'mentors' ? (
        <div className="space-y-3">
          {mentors.map((item) => (
            <div key={item.mentorId} className="rounded-xl border bg-white p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <p className="font-semibold text-gray-900">{item.fullName} <span className="text-sm font-normal text-gray-500">{item.contactEmail}</span></p>
                <p className="text-sm text-gray-600">{item.yearsOfExperience} years · {item.primaryExpertiseArea} · {item.approvalStatus === 'Pending' ? 'Pending Review' : (item.approvalStatus || 'Pending Review')}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {item.mentorId ? (
                  <>
                    <Button size="sm" variant="outline" onClick={() => openMentor(item.mentorId!, 'view')}>View</Button>
                    {canEditMentors && <Button size="sm" variant="outline" onClick={() => openMentor(item.mentorId!, 'edit')}>Edit</Button>}
                  </>
                ) : null}
                {canApproveMentors(user) && item.approvalStatus !== 'Approved' && (
                  <>
                    <Button size="sm" onClick={() => item.mentorId && approveMentor(item.mentorId)}>Approve</Button>
                    <Button size="sm" variant="outline" onClick={() => item.mentorId && rejectMentor(item.mentorId)}>Reject</Button>
                  </>
                )}
              </div>
              <ActionStatus message={actionKey === `mentor-${item.mentorId}` ? message : null} />
            </div>
          ))}
          {canAssignProgramMentors(user) && (
            <div className="rounded-xl border bg-white p-4 space-y-3">
              <h3 className="font-semibold text-gray-900">Assign mentors to a program</h3>
              <select className="border rounded p-2" value={selectedProgram} onChange={(e) => {
                const id = e.target.value ? Number(e.target.value) : '';
                setSelectedProgram(id);
                if (id) loadAssigned(Number(id));
              }}>
                <option value="">Select a program</option>
                {programs.map((program) => <option key={program.id} value={program.id}>{program.title}</option>)}
              </select>
              {selectedProgram && (
                <div className="space-y-2">
                  {mentors.filter((item) => item.approvalStatus === 'Approved').map((item) => (
                    <div key={`assign-${item.mentorId}`} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span>{item.fullName}</span>
                        <Button size="sm" variant="outline" onClick={() => item.mentorId && mentorApi.assignProgramMentor(Number(selectedProgram), item.mentorId).then(() => { setActionKey(`assign-${item.mentorId}`); setMessage('Mentor assigned.'); loadAssigned(Number(selectedProgram)); })}>Assign</Button>
                      </div>
                      {actionKey === `assign-${item.mentorId}` ? <ActionStatus className="mt-1" message={message} /> : null}
                    </div>
                  ))}
                  <p className="text-sm text-gray-500">Currently assigned: {assigned.map((item) => item.fullName).filter(Boolean).join(', ') || 'none'}</p>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {interviewers.map((item) => (
            <div key={item.interviewerId} className="rounded-xl border bg-white p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <p className="font-semibold text-gray-900">{item.fullName} <span className="text-sm font-normal text-gray-500">{item.contactEmail}</span></p>
                <p className="text-sm text-gray-600">{item.yearsOfExperience} years · {(item.technologies || []).join(', ')} · {(item.approvalStatus === 'Pending' ? 'Pending Review' : item.approvalStatus) || 'Pending Review'}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => openInterviewer(item.interviewerId, 'view')}>View</Button>
                {canEditInterviewers && <Button size="sm" variant="outline" onClick={() => openInterviewer(item.interviewerId, 'edit')}>Edit</Button>}
                {canApproveInterviewers(user) && item.approvalStatus !== 'Approved' && (
                  <>
                    <Button size="sm" onClick={() => approveInterviewer(item.interviewerId)}>Approve</Button>
                    <Button size="sm" variant="outline" onClick={() => rejectInterviewer(item.interviewerId)}>Reject</Button>
                  </>
                )}
              </div>
              <ActionStatus message={actionKey === `interviewer-${item.interviewerId}` ? message : null} />
            </div>
          ))}
        </div>
      )}

      <ContributorProfileEditor
        kind={kind}
        open={editorOpen}
        mode={editorMode}
        mentor={editorMentor}
        interviewer={editorInterviewer}
        saving={savingProfile}
        error={error}
        onClose={() => setEditorOpen(false)}
        onSaveMentor={saveMentor}
        onSaveInterviewer={saveInterviewer}
      />
    </div>
  );
};

export default ContributorApplicationsPanel;
